/**
 * LEGACY schema-property renderer shim (EAINT-855).
 *
 * NOT a general-purpose "design system component renderer" despite the name, and NOT part of the
 * composer's component registry. Its whole job is PROP REMAPPING: binding a component to one schema
 * property's value via `valueProp` / `onChangeProp` / `onEditProp` / `valueAccessor` / `changeMapper`
 * (including `{type:'multiField'}`, which fans one payload across several form paths — semantics a
 * plain `{value,onChange}` contract cannot express).
 *
 * REACH IT ONLY THROUGH `resolveRenderer` / `UnifiedPropertyRendererMolecule`. Both former direct
 * importers (`AdditionalPropertiesGrid`, `SphereEditor`) were migrated off it; a direct import
 * re-creates the parallel resolution path whose drift EAINT-855 existed to remove — that is how the
 * grid and the field-input molecule came to disagree, and how `editComponent` became dead code.
 *
 * It is deliberately NOT folded into the composition-host `adapterRegistry`: the catalog and that
 * registry describe PLACEABLE components aligned 1:1 by `type`, three of the five components below
 * are not in the catalog at all, and the `Adapter` convention `(componentId, props, ctx)` cannot
 * express the wiring above without re-creating this file under another name. See the decision note
 * in `specs/aspect-default-components.md` under "Renderer contract".
 *
 * No `console.debug` here: this renders on the property-grid hot path.
 */
import React from 'react';
// Import your registry of components
import { SchedulesEditorMolecule } from './molecules/SchedulesEditor/SchedulesEditorMolecule';
import ScheduleSelectorMolecule from './molecules/ScheduleSelector/ScheduleSelectorMolecule';
import DataGridAtom from './atoms/DataGrid/DataGridAtom';
import ChipListDisplayAtom from './atoms/ChipListDisplay/ChipListDisplayAtom';

// TreeMap is loaded lazily, not statically imported, because it closes a real
// circular dependency: TreeMap -> EditSphere -> SphereEditor ->
// DesignSystemComponentRenderer -> TreeMap. A static import here makes
// DesignSystemComponentRenderer's module-scope componentMap read TreeMap's
// binding before TreeMap.tsx finishes evaluating whenever TreeMap sits at the
// top of the module graph (e.g. TreeMap.stories.tsx), which throws
// "Cannot access 'TreeMap' before initialization" under strict ESM evaluation
// order (surfaced by Vitest's browser runner; the old webpack-based
// test-runner never evaluated this graph).
const TreeMap = React.lazy(() => import('./organisms/TreeMap/TreeMap'));

const componentMap: Record<string, React.ElementType> = {
  SchedulesEditorMolecule,
  ScheduleSelectorMolecule,
  DataGridAtom,
  ChipListDisplayAtom,
  TreeMap,
  // add more here...
};

/**
 * Whether `componentName` is one this renderer can actually mount. `resolveRenderer`'s legacy
 * compat step (EAINT-855 chain step 3) consults this so an `editComponent`/`displayComponent`
 * naming a component that was never registered here FALLS THROUGH to the type default instead of
 * resolving to a shim that renders `null` — the spec's "never blank" rule
 * (specs/aspect-default-components.md §Edge cases).
 */
export function isLegacyComponentRegistered(componentName: string | undefined): boolean {
  return !!componentName && componentName in componentMap;
}

export type ChangeMapper =
  | ((changed: any, current: any) => any) // Original function type
  | {
      // NEW: Multi-field mapper object
      type: 'multiField';
      mappings: Record<string, string>; // fieldName -> payloadPath
    };

export type ValueAccessor =
  | string // Original string path
  | {
      // NEW: Multi-field accessor object
      type: 'multiField';
      mappings: Record<string, string>; // componentProp -> formFieldPath
    };

export type RendererProps = {
  /** Name of the component to render (must exist in componentMap) */
  componentName: string;

  /** The current value to pass to the component */
  value: any;

  /** Callback when the component value changes */
  onChange: (newValue: any) => void;

  /** Optional edit callback (for display components that support editing) */
  onEdit?: () => void;

  /** Additional props to pass to the component */
  componentProps?: Record<string, any>;

  /**
   * Optional path to drill into the value object (dot-separated)
   * e.g., "rrule" or "schedule.rrule"
   * OR multi-field mapping object
   */
  valueAccessor?: ValueAccessor;

  /**
   * Optional mapping function from child payload back into your form value.
   * Called as `mapper(childPayload, currentValue)` OR multi-field mapping object
   */
  changeMapper?: ChangeMapper;
};

export const DesignSystemComponentRenderer: React.FC<RendererProps> = ({
  componentName,
  value,
  onChange,
  onEdit,
  componentProps = {},
  valueAccessor,
  changeMapper,
}) => {
  // Check if component exists
  if (!componentName || !(componentName in componentMap)) {
    console.warn('[DSCR] Component not found:', componentName);
    return null;
  }

  const Component = componentMap[componentName];

  // Extract wiring directives from componentProps
  const { valueProp = 'value', onChangeProp = 'onChange', onEditProp = 'onEdit', ...restProps } = componentProps;

  // NEW: Build coordinated input value for multi-field components
  const buildCoordinatedValue = () => {
    // Handle multi-field valueAccessor
    if (valueAccessor && typeof valueAccessor === 'object' && valueAccessor.type === 'multiField') {
      const coordinatedValue: any = {};

      // Map form fields to component props
      Object.entries(valueAccessor.mappings).forEach(([componentProp, formFieldPath]) => {

        const formValue = safeAccess(value, formFieldPath);

        if (formValue !== undefined) {
          coordinatedValue[componentProp] = formValue;
        }
      });

      return coordinatedValue;
    }

    // Handle legacy changeMapper multi-field (for backward compatibility)
    if (changeMapper && typeof changeMapper === 'object' && changeMapper.type === 'multiField') {
      const coordinatedValue: any = {};

      // Reverse the mapping: read from form fields and build component value
      Object.entries(changeMapper.mappings).forEach(([formFieldPath, componentPath]) => {
        const formValue = safeAccess(value, formFieldPath);

        if (formValue !== undefined) {
          setNestedValue(coordinatedValue, componentPath, formValue);
        }
      });

      return coordinatedValue;
    }

    // Fallback to original logic
    if (typeof valueAccessor === 'string') {
      return safeAccess(value, valueAccessor);
    }

    return value;
  };

  const mappedValue = buildCoordinatedValue();

  // Build our final onChange handler
  const handleChange = (childPayload: any) => {

    let newValue = childPayload;

    // Handle multi-field change mapper
    if (changeMapper && typeof changeMapper === 'object' && changeMapper.type === 'multiField') {

      // Create a deep copy of the current value to avoid mutations
      newValue = JSON.parse(JSON.stringify(value || {}));

      // Apply each mapping from payload to form field
      Object.entries(changeMapper.mappings).forEach(([formFieldPath, payloadPath]) => {
        const mappedValue = safeAccess(childPayload, payloadPath);

        // Directly set the field in the cloned value
        setNestedValue(newValue, formFieldPath, mappedValue);
      });

    } else if (typeof changeMapper === 'function') {
      // Original function-based mapper
      newValue = changeMapper(childPayload, value);
    }
    // If no changeMapper, use childPayload as-is (original behavior)

    onChange(newValue);
  };

  // Build final props object
  const finalProps: Record<string, any> = {
    ...restProps,
  };

  // Handle the value prop - could be a single value or multiple props from coordinated value
  if (
    typeof mappedValue === 'object' &&
    mappedValue !== null &&
    !Array.isArray(mappedValue) &&
    !(mappedValue instanceof Date)
  ) {
    // If mappedValue is an object with multiple properties, spread them as individual props
    Object.assign(finalProps, mappedValue);

    // Also set the main value prop if it doesn't conflict
    if (!(valueProp in mappedValue)) {
      finalProps[valueProp] = mappedValue;
    }
  } else {
    // Single value
    finalProps[valueProp] = mappedValue;
  }

  finalProps[onChangeProp] = handleChange;

  // Only add onEdit if both the prop is provided and the component supports it
  if (onEdit && onEditProp) {
    finalProps[onEditProp] = onEdit;
  }


  return (
    <React.Suspense fallback={null}>
      <Component {...finalProps} />
    </React.Suspense>
  );
};

/**
 * Safely drill into an object via a dot-separated path.
 * E.g. safeAccess(obj, "foo.bar.baz")
 */
function safeAccess(obj: any, path: string): any {
  // Add type checking to prevent errors
  if (!path || typeof path !== 'string') {
    console.warn('[DSCR] safeAccess called with invalid path:', path);
    return obj;
  }

  if (!obj) {
    return undefined;
  }

  return path.split('.').reduce((o, key) => (o != null ? o[key] : undefined), obj);
}

/**
 * Set a nested value in an object using dot notation
 * e.g., setNestedValue(obj, "properties.startDate", value)
 */
function setNestedValue(obj: any, path: string, value: any): void {
  if (!path || typeof path !== 'string') {
    console.warn('[DSCR] setNestedValue called with invalid path:', path);
    return;
  }

  const keys = path.split('.');
  const lastKey = keys.pop()!;

  // Navigate to the parent object, creating nested objects as needed
  const parent = keys.reduce((current, key) => {
    if (current[key] === undefined) {
      current[key] = {};
    }
    return current[key];
  }, obj);

  // Set the final value
  parent[lastKey] = value;
}
